import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import { ExpandMore, ExpandLess, Visibility, Payment } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { mockTransactionsData } from "../../mocks/transactionsMockData";

const TransactionWeekCard = ({ weekData }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    return status === "PAID" ? "success" : "primary";
  };

  // Calculate total penalties for the week (amount only, no count)
  const totalPenalties = weekData.dailySummaries.reduce(
    (sum, day) => sum + Math.abs(day.penaltyAmount),
    0
  );

  const handleGetInvoice = () => {
    const weekDates = (weekData.dailySummaries || []).map((d) => d.date);
    const transactions = (mockTransactionsData.detailedTransactions || []).filter(
      (txn) => weekDates.includes(txn.date)
    );

    navigate("/transaction-report", {
      state: { transactions, date: weekData.weekLabel.replace("Week: ", "") },
    });
  };

  // ✅ Updated: Navigate to all-transactions page with date filter
  const handleViewTransactions = (day) => {
    navigate("/all-transactions", {
      state: {
        date: day.date,
      },
    });
  };

  const handleRequestPayout = (day) => {
    // Add your payout request logic here
    console.log("Request payout for:", day.date);
    // Example: Show modal, call API, etc.
  };

  return (
    <Paper sx={{ borderRadius: 2, overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
      {/* Card Content */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "background.paper",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            bgcolor: theme.palette.mode === "dark" ? "grey.800" : "grey.50",
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Week Period Column */}
        <Box sx={{ flex: "0 0 250px" }}>
          <Typography variant="body2">
            {weekData.weekLabel.replace("Week: ", "")}
          </Typography>
        </Box>

        {/* Right Columns */}
        <Box display="flex" alignItems="center" gap={3}>
          {/* Transactions Column */}
          <Box sx={{ width: "100px", textAlign: "center" }}>
            <Typography variant="body2">
              {weekData.transactionCount}
            </Typography>
          </Box>

          {/* Penalties Column - Amount only with minus sign */}
          <Box sx={{ width: "120px", textAlign: "center" }}>
            {totalPenalties > 0 ? (
              <Typography variant="body2" color="error.main" fontWeight="medium">
                -₹{totalPenalties.toFixed(2)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>

          {/* Total Amount Column */}
          <Box sx={{ width: "120px", textAlign: "right" }}>
            <Typography variant="body2" color="success.main">
              ₹{weekData.totalAmount.toFixed(2)}
            </Typography>
          </Box>

          <Box sx={{ width: "100px", display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Chip
              sx={{ minWidth: 80 }}
              label={weekData.status}
              color={getStatusColor(weekData.status)}
              size="small"
            />
            <IconButton size="small">
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

        </Box>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {isMobile ? (
          // Mobile: Card View
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              {weekData.dailySummaries.map((day, index) => (
                <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                  <Stack spacing={0.75}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body2">
                        {day.date}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Transactions
                      </Typography>
                      <Typography variant="body2">
                        {day.transactionCount}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Total Earnings
                      </Typography>
                      <Typography variant="body2">
                        ₹{day.totalEarnings.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">
                        Penalties
                      </Typography>
                      <Typography variant="body2" color="error.main" fontWeight="medium">
                        {day.penaltyAmount !== 0 ? `-₹${Math.abs(day.penaltyAmount).toFixed(2)}` : "-"}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Net Amount
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        ₹{day.netAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewTransactions(day);
                        }}
                        fullWidth
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Payment />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestPayout(day);
                        }}
                        fullWidth
                      >
                        Payout
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        ) : (
          // Desktop: Table View
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="center">Transactions</TableCell>
                  <TableCell align="right">Total Earnings</TableCell>
                  <TableCell align="center">Penalties</TableCell>
                  <TableCell align="right">Net Amount</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {weekData.dailySummaries.map((day, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td": { border: 0 },
                      "&:hover": { bgcolor: theme.palette.action.hover },
                    }}
                  >
                    <TableCell>{day.date}</TableCell>
                    <TableCell align="center">{day.transactionCount}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        ₹{day.totalEarnings.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {day.penaltyAmount !== 0 ? (
                        <Typography variant="body2" color="error.main" fontWeight="medium">
                          -₹{Math.abs(day.penaltyAmount).toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="success.main" fontWeight="medium">
                        ₹{day.netAmount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTransactions(day);
                          }}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Payment />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRequestPayout(day);
                          }}
                        >
                          Request Payout
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleGetInvoice();
            }}
          >
            Weekly Invoice
          </Button>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TransactionWeekCard;
